import { Component, inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { CentrexDevicesService } from 'src/app/services/centrex-device.service';
import { CentrexDeviceModel } from 'src/app/models/centrex-device.model';
import { AccessRightsService } from 'src/app/services/access-rights.service';
import { DevicesFormComponent } from './devices-form/devices-form.component';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { CentrexSitesService } from 'src/app/services/centrex-sites.service';
import { OrganizationsService } from 'src/app/services/organization.service';
import { MatSelectModule } from '@angular/material/select';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { finalize } from 'rxjs';
import { PlatformService } from 'src/app/services/platform.service';

@Component({
  selector: 'app-devices',
  templateUrl: './devices.component.html',
  styleUrls: ['./devices.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatButtonModule,
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatPaginator,
    MatTableModule,
    MatSelectModule,
    MatInputModule,
    NgxSpinnerModule,
    MatSortModule,
  ],
})
export class DevicesComponent implements OnInit {
  platform = this.platformService.getCurrentPlatform();
  filtersForm: FormGroup;
  displayedColumns: string[] = [
    'name',
    'site',
    'modelName',
    'address',
    'extension',
    'sda',
    'state',
    'actions',
  ];
  dataSource = new MatTableDataSource<CentrexDeviceModel>([]);
  isFullAccess = false;
  organizationId: string | null = null;

  availableSites: any[] = [];
  originalDevices: CentrexDeviceModel[] = [];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  private spinner = inject(NgxSpinnerService);

  constructor(
    private fb: FormBuilder,
    private devicesService: CentrexDevicesService,
    private dialog: MatDialog,
    private accessRightsService: AccessRightsService,
    private sitesService: CentrexSitesService,
    private organizationsService: OrganizationsService,
    private platformService: PlatformService,
  ) {
    this.filtersForm = this.fb.group({
      name: [''],
      site: [''],
      modelName: [''],
      address: [''],
      extension: [''],
      sda: [''],
    });
  }

  ngOnInit(): void {
    // Gestion des droits
    this.accessRightsService
      .hasNexusCentrexFullAccess()
      .subscribe((isAllowed) => (this.isFullAccess = isAllowed));

    // Écoute changement organisation
    this.organizationsService.currentOrganizationChange$.subscribe((org) => {
      if (org) {
        this.organizationId = org.id;
        this.loadDevices();
      }
    });

    // Organisation initiale
    // const currentOrg = this.organizationsService.getCurrentOrganization();
    // if (currentOrg) {
    //   this.organizationId = currentOrg.id;
    //   this.loadDevices();
    // }
  }

  loadDevices(): void {
    if (!this.organizationId) return;

    this.spinner.show('spinner');

    this.devicesService
      .getDevices(this.organizationId, this.platform)
      .pipe(
        finalize(() => {
          this.spinner.hide('spinner');
        }),
      )
      .subscribe((devices) => {
        this.sitesService
          .getSites(this.organizationId!, this.platform)
          .subscribe((sites) => {
            this.availableSites = sites;

            const siteMap = new Map(
              sites.map((s: any) => [String(s.siteID || s.siteId), s.name]),
            );

            const enrichedDevices = devices.map((device: any) => ({
              ...device,
              siteName: siteMap.get(String(device.siteName)) || '—',
            }));

            this.refreshDataSource(enrichedDevices);
          });
      });
  }

  refreshDataSource(devices: CentrexDeviceModel[]) {
    this.originalDevices = [...devices]; // sauvegarde brute
    this.dataSource.data = [...devices]; // affichage filtrable
    this.dataSource.paginator = this.paginator;
  }

  onPlatformChange(): void {
    this.platformService.setPlatform(this.platform);
    this.loadDevices();
  }

  applyFilters(): void {
    const filters = this.filtersForm.value;

    this.dataSource.data = this.originalDevices.filter((device) => {
      return Object.keys(filters).every((key) => {
        const searchValue = (filters[key] || '')
          .toString()
          .trim()
          .toLowerCase();

        // Cas particulier pour extension (tableau)
        if (key === 'extension') {
          const extensionsString =
            device.extensionIds?.join(', ').toLowerCase() || '';
          return !searchValue || extensionsString.includes(searchValue);
        }

        const fieldValue =
          (device as any)[key]?.toString().trim().toLowerCase() || '';
        return !searchValue || fieldValue.includes(searchValue);
      });
    });
  }

  resetColumnFilter(column: string): void {
    this.filtersForm.get(column)?.setValue('');
    this.applyFilters();
  }

  onView(device: CentrexDeviceModel): void {
    this.dialog.open(DevicesFormComponent, {
      width: '800px',
      data: {
        device,
        edit: false,
        organizationId: this.organizationId,
        platform: this.platform,
        sites: this.availableSites,
      },
    });
  }

  onEdit(device: CentrexDeviceModel): void {
    this.dialog
      .open(DevicesFormComponent, {
        width: '800px',
        data: {
          device,
          edit: true,
          organizationId: this.organizationId,
          platform: this.platform,
          sites: this.availableSites,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.loadDevices();
        }
      });
  }
}
